<script setup>
import { ref, nextTick, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  authError: { type: String, default: '' },
  target: { type: String, default: 'web' },
});

const store = useStore();
const { t } = useI18n();

const credentials = ref({ email: '' });
const loginApi = ref({ showLoading: false, hasErrored: false });

const handleAuthError = () => {
  if (!props.authError) return;
  useAlert(t('LOGIN.SAML.API.ERROR_MESSAGE'));
  loginApi.value.hasErrored = true;
};

const validations = {
  credentials: { email: { required, email } },
};
const v$ = useVuelidate(validations, { credentials });
const globalConfig = computed(() => store.getters['globalConfig/get']);
const csrfToken = ref('');

onMounted(async () => {
  csrfToken.value =
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute('content') || '';
  await nextTick(handleAuthError);
});
</script>

<template>
  <div class="sp-auth-page">
    <!-- Left visual panel -->
    <div class="sp-auth-visual">
      <div class="sp-grid-bg" />
      <div class="sp-glow-orb sp-glow-1" />
      <div class="sp-glow-orb sp-glow-2" />
      <div class="sp-glow-orb sp-glow-3" />
      <div class="sp-particle sp-particle-1" />
      <div class="sp-particle sp-particle-2" />
      <div class="sp-particle sp-particle-3" />
      <div class="sp-particle sp-particle-4" />
      <div class="sp-particle sp-particle-5" />

      <div class="sp-visual-inner">
        <!-- Badge -->
        <div class="sp-animate-in" style="margin-bottom: 16px">
          <div class="sp-badge">
            <span class="sp-badge-dot" />
            Enterprise-grade SSO
          </div>
        </div>

        <!-- Hero text -->
        <div
          class="sp-animate-in sp-delay-1"
          style="text-align: center; margin-bottom: 28px"
        >
          <h1 class="sp-hero-text" style="font-size: 52px">
            Secure<br />
            <span class="sp-gradient-text">Sign-On.</span>
          </h1>
          <p class="sp-hero-sub">
            Sign in with your organization's identity provider. One login, full
            access.
          </p>
        </div>

        <!-- SSO features -->
        <div
          class="sp-animate-in sp-delay-2"
          style="
            width: 100%;
            max-width: 380px;
            display: flex;
            flex-direction: column;
            gap: 10px;
            margin-bottom: 16px;
          "
        >
          <div
            class="sp-glass-card"
            style="
              padding: 14px 18px;
              display: flex;
              align-items: center;
              gap: 14px;
            "
          >
            <div
              class="sp-feature-icon"
              style="
                width: 40px;
                height: 40px;
                flex-shrink: 0;
                background: linear-gradient(135deg, #6366f1, #8b5cf6);
              "
            >
              <svg
                width="18"
                height="18"
                fill="none"
                stroke="white"
                stroke-width="2"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                />
              </svg>
            </div>
            <div>
              <div
                style="
                  font-size: 13px;
                  font-weight: 800;
                  color: #0f172a;
                  margin-bottom: 2px;
                "
              >
                SAML 2.0 / OIDC
              </div>
              <div style="font-size: 11px; color: #64748b">
                Works with Okta, Azure AD, Google Workspace, and more.
              </div>
            </div>
          </div>
          <div
            class="sp-glass-card"
            style="
              padding: 14px 18px;
              display: flex;
              align-items: center;
              gap: 14px;
            "
          >
            <div
              class="sp-feature-icon"
              style="
                width: 40px;
                height: 40px;
                flex-shrink: 0;
                background: linear-gradient(135deg, #8b5cf6, #a855f7);
              "
            >
              <svg
                width="18"
                height="18"
                fill="none"
                stroke="white"
                stroke-width="2"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
            </div>
            <div>
              <div
                style="
                  font-size: 13px;
                  font-weight: 800;
                  color: #0f172a;
                  margin-bottom: 2px;
                "
              >
                Zero-trust Security
              </div>
              <div style="font-size: 11px; color: #64748b">
                Every login verified by your identity provider.
              </div>
            </div>
          </div>
          <div
            class="sp-glass-card"
            style="
              padding: 14px 18px;
              display: flex;
              align-items: center;
              gap: 14px;
            "
          >
            <div
              class="sp-feature-icon"
              style="
                width: 40px;
                height: 40px;
                flex-shrink: 0;
                background: linear-gradient(135deg, #6366f1, #a855f7);
              "
            >
              <svg
                width="18"
                height="18"
                fill="none"
                stroke="white"
                stroke-width="2"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </div>
            <div>
              <div
                style="
                  font-size: 13px;
                  font-weight: 800;
                  color: #0f172a;
                  margin-bottom: 2px;
                "
              >
                Auto-provisioning
              </div>
              <div style="font-size: 11px; color: #64748b">
                Team members are automatically added when they sign in.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right form panel -->
    <div class="sp-auth-form-side">
      <div class="sp-auth-form-container">
        <!-- Logo -->
        <div
          class="sp-animate-in"
          style="text-align: center; margin-bottom: 24px"
        >
          <img
            :src="globalConfig.logo"
            :alt="globalConfig.installationName"
            style="height: 56px; display: inline-block"
            class="dark:hidden"
          />
          <img
            v-if="globalConfig.logoDark"
            :src="globalConfig.logoDark"
            :alt="globalConfig.installationName"
            style="height: 56px; display: none"
            class="dark:inline-block"
          />
        </div>

        <!-- Heading -->
        <div
          class="sp-animate-in sp-delay-1"
          style="margin-bottom: 24px; text-align: center"
        >
          <h2
            style="
              font-size: 22px;
              font-weight: 800;
              color: #0f172a;
              letter-spacing: -0.03em;
              margin-bottom: 6px;
            "
          >
            {{ t('LOGIN.SAML.TITLE') }}
          </h2>
          <p style="font-size: 13px; color: #64748b; line-height: 1.5">
            Enter Your Work Email to Continue with SSO.
          </p>
        </div>

        <form
          class="sp-animate-in sp-delay-3"
          :class="{ 'animate-wiggle': loginApi.hasErrored }"
          method="POST"
          action="/api/v1/auth/saml_login"
        >
          <div class="sp-field">
            <label class="sp-label">{{
              t('LOGIN.SAML.WORK_EMAIL.LABEL')
            }}</label>
            <input
              v-model="credentials.email"
              name="email"
              type="email"
              class="sp-input"
              :class="{ 'sp-input-error': v$.credentials.email.$error }"
              :tabindex="1"
              :placeholder="t('LOGIN.SAML.WORK_EMAIL.PLACEHOLDER')"
              @input="v$.credentials.email.$touch"
            />
          </div>
          <input
            type="hidden"
            class="h-0"
            name="authenticity_token"
            :value="csrfToken"
          />
          <input type="hidden" class="h-0" name="target" :value="target" />
          <button
            type="submit"
            class="sp-btn"
            style="margin-top: 16px"
            :disabled="loginApi.showLoading"
          >
            <span v-if="!loginApi.showLoading">{{
              t('LOGIN.SAML.SUBMIT')
            }}</span>
            <span v-else>Redirecting...</span>
          </button>
        </form>

        <p
          class="sp-animate-in sp-delay-4"
          style="
            text-align: center;
            margin-top: 16px;
            font-size: 12px;
            color: #64748b;
          "
        >
          <router-link to="/app/login" class="sp-link">
            ← {{ t('LOGIN.SAML.BACK_TO_LOGIN') }}
          </router-link>
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes sp-particle-float {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
    opacity: 0.4;
  }
  25% {
    transform: translate(20px, -30px) scale(1.5);
    opacity: 0.8;
  }
  50% {
    transform: translate(-10px, -60px) scale(1);
    opacity: 0.6;
  }
  75% {
    transform: translate(15px, -20px) scale(1.3);
    opacity: 0.9;
  }
}
@keyframes sp-float-drift {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(30px, -20px) scale(1.05);
  }
  66% {
    transform: translate(-20px, 15px) scale(0.95);
  }
}
@keyframes sp-slide-in {
  from {
    opacity: 0;
    transform: translateY(32px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes sp-pulse-soft {
  0%,
  100% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
}
@keyframes sp-shimmer-slide {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.sp-auth-page {
  display: flex;
  height: 100vh;
  width: 100%;
  background: #ffffff;
  overflow: hidden;
}
.sp-auth-visual {
  display: none;
  width: 50%;
  position: relative;
  overflow: hidden;
  background: linear-gradient(
    145deg,
    #f5f3ff 0%,
    #ede9fe 25%,
    #e0e7ff 50%,
    #ddd6fe 75%,
    #f0f9ff 100%
  );
}
@media (min-width: 1024px) {
  .sp-auth-visual {
    display: flex;
  }
}
.sp-auth-form-side {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #ffffff;
  overflow-y: auto;
  height: 100vh;
}
@media (min-width: 1024px) {
  .sp-auth-form-side {
    padding: 24px 48px;
  }
}
@media (min-width: 1280px) {
  .sp-auth-form-side {
    padding: 24px 64px;
  }
}
.sp-auth-form-container {
  width: 100%;
  max-width: 440px;
}
.sp-grid-bg {
  position: absolute;
  inset: 0;
  background-image: linear-gradient(
      rgba(99, 102, 241, 0.05) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(99, 102, 241, 0.05) 1px, transparent 1px);
  background-size: 40px 40px;
}
.sp-glow-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
}
.sp-glow-1 {
  width: 500px;
  height: 500px;
  background: rgba(139, 92, 246, 0.3);
  top: -120px;
  right: -100px;
  animation: sp-float-drift 8s ease-in-out infinite;
}
.sp-glow-2 {
  width: 450px;
  height: 450px;
  background: rgba(99, 102, 241, 0.25);
  bottom: -60px;
  left: -80px;
  animation: sp-float-drift 10s ease-in-out infinite reverse;
}
.sp-glow-3 {
  width: 300px;
  height: 300px;
  background: rgba(59, 130, 246, 0.2);
  top: 35%;
  left: 25%;
  animation: sp-float-drift 12s ease-in-out infinite 2s;
}
.sp-particle {
  position: absolute;
  border-radius: 50%;
  background: rgba(99, 102, 241, 0.4);
  pointer-events: none;
}
.sp-particle-1 {
  width: 6px;
  height: 6px;
  top: 15%;
  left: 20%;
  animation: sp-particle-float 6s ease-in-out infinite;
}
.sp-particle-2 {
  width: 4px;
  height: 4px;
  top: 35%;
  right: 15%;
  animation: sp-particle-float 8s ease-in-out infinite 1s;
}
.sp-particle-3 {
  width: 8px;
  height: 8px;
  bottom: 25%;
  left: 30%;
  animation: sp-particle-float 7s ease-in-out infinite 2s;
}
.sp-particle-4 {
  width: 5px;
  height: 5px;
  top: 60%;
  right: 25%;
  animation: sp-particle-float 9s ease-in-out infinite 0.5s;
}
.sp-particle-5 {
  width: 3px;
  height: 3px;
  top: 80%;
  left: 15%;
  animation: sp-particle-float 5s ease-in-out infinite 3s;
}
.sp-visual-inner {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 28px 36px;
}
.sp-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 16px;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 100px;
  font-size: 11px;
  font-weight: 700;
  color: #6366f1;
  border: 1px solid rgba(99, 102, 241, 0.15);
  backdrop-filter: blur(12px);
}
.sp-badge-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6366f1, #a855f7);
  animation: sp-pulse-soft 2s infinite;
}
.sp-hero-text {
  font-size: 60px;
  font-weight: 900;
  line-height: 1;
  color: #0f172a;
  letter-spacing: -0.04em;
  margin-bottom: 10px;
}
.sp-hero-sub {
  font-size: 14px;
  color: #475569;
  line-height: 1.5;
  max-width: 340px;
  margin: 0 auto;
  text-align: center;
}
.sp-gradient-text {
  background: linear-gradient(135deg, #4f46e5, #7c3aed, #a855f7);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.sp-glass-card {
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 20px;
  box-shadow:
    0 4px 24px rgba(0, 0, 0, 0.04),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
}
.sp-feature-icon {
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sp-animate-in {
  animation: sp-slide-in 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  opacity: 0;
}
.sp-delay-1 {
  animation-delay: 0.1s;
}
.sp-delay-2 {
  animation-delay: 0.2s;
}
.sp-delay-3 {
  animation-delay: 0.3s;
}
.sp-delay-4 {
  animation-delay: 0.4s;
}
.sp-field {
  display: flex;
  flex-direction: column;
}
.sp-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 8px;
  letter-spacing: -0.01em;
}
.sp-input {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  color: #0f172a;
  background: #ffffff;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  outline: none;
  font-family:
    'Inter',
    -apple-system,
    sans-serif;
}
.sp-input:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.12);
}
.sp-input::placeholder {
  color: #94a3b8;
  font-weight: 400;
}
.sp-input-error {
  border-color: #f43f5e;
  box-shadow: 0 0 0 4px rgba(244, 63, 94, 0.1);
}
.sp-btn {
  width: 100%;
  padding: 12px 24px;
  background: linear-gradient(135deg, #4f46e5 0%, #6366f1 50%, #7c3aed 100%);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-family:
    'Inter',
    -apple-system,
    sans-serif;
  letter-spacing: -0.01em;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.3);
}
.sp-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 32px rgba(99, 102, 241, 0.45);
}
.sp-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}
.sp-btn::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.15),
    transparent
  );
  animation: sp-shimmer-slide 3s infinite;
}
.sp-link {
  color: #6366f1;
  font-weight: 600;
  text-decoration: none;
  transition: color 0.2s;
}
.sp-link:hover {
  color: #4f46e5;
}
</style>
